import React, { useEffect, useState, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { format } from 'date-fns';
import { getPEInventoryMetrics, getPEProductionCapacity, getCachedACCFinalData } from '@/services/marketIntelService';
import type { PEInventoryMetricsItem, PEProductionCapacityItem } from '@/types/pe.types';
import { PEMetricFilter, PE_METRIC_LABELS } from '@/types/pe.types';
import ChartDateLimiter from '@/components/charts/ChartDateLimiter';
import { getChartFontSize } from '@/utils/chartTypography';
import { parseDateLiteral, inventoryChangeAxisLabel, isPastThreeYearMark, isWithinThreeYearRange } from '@/utils/chartAxisUtils';
import { useRsColors } from '@/hooks/useRsColors';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';
import { useTheme } from '@/contexts/ThemeContext';
import FreeTrialFade from '@/components/common/FreeTrialFade';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type ResinSubType = 'pe' | 'hdpe' | 'ldpe' | 'lldpe';

interface PETotalsTabProps {
  resinType?: 'pe' | 'hdpe' | 'ldpe' | 'lldpe';
  activeMetric?: PEMetricFilter;
  dateFrom?: string;
  dateTo?: string;
  inventoryChangeOnly?: boolean;
  fullBleed?: boolean;
  isOverviewPage?: boolean;
}

const SUB_TYPES: { key: ResinSubType; label: string }[] = [
  { key: 'pe', label: 'ALL PE' },
  { key: 'hdpe', label: 'HDPE' },
  { key: 'ldpe', label: 'LDPE' },
  { key: 'lldpe', label: 'LLDPE' },
];

const METRIC_TABS = [
  PEMetricFilter.DAYS_INVENTORY,
  PEMetricFilter.DEMAND_RATE,
  PEMetricFilter.DOMESTIC_DEMAND,
  PEMetricFilter.DOMESTIC_PRODUCTION,
  PEMetricFilter.EXPORTS,
  PEMetricFilter.INVENTORY_CHANGE,
  PEMetricFilter.OPERATING_RATES,
  PEMetricFilter.TOTAL_SALES,
]

interface MetricChartProps {
  metric: PEMetricFilter;
  labels: string[];
  metrics: PEInventoryMetricsItem[];
  prodCap: PEProductionCapacityItem[];
}

const MetricChart: React.FC<MetricChartProps> = ({ metric, labels, metrics, prodCap }) => {
  const rs = useRsColors();
  const { isDark } = useTheme();
  // Below 1024px, hide all chart data labels.
  const [hideDataLabels, setHideDataLabels] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e: MediaQueryListEvent) => setHideDataLabels(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const CHART_DEFAULTS = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: { color: rs.offWhite, font: { size: getChartFontSize() } },
        grid: { color: rs.gridLine },
      },
      y: {
        ticks: { color: rs.offWhite, font: { size: getChartFontSize() } },
        grid: { color: rs.gridLine },
      },
    },
  }), [rs]);

  const formatMillions = (billionValue: number) => {
    const millions = billionValue * 1000;
    const abs = Math.abs(millions);
    if (abs >= 1000) return `${(millions / 1000).toFixed(1)}B`;
    return `${Math.round(millions)}M`;
  };

  const formatBillions = (value: number) => {
    const abs = Math.abs(value);
    const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return `${Number(value.toFixed(digits))}B`;
  };

  const formatMillionsFromBillions = (value: number) => {
    const inMillions = value * 1000;
    const abs = Math.abs(inMillions);
    const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return `${Number(inMillions.toFixed(digits))}M`;
  };

  const millionMetrics = new Set<PEMetricFilter>([
    PEMetricFilter.INVENTORY_CHANGE,
    PEMetricFilter.DOMESTIC_PRODUCTION,
    PEMetricFilter.DOMESTIC_DEMAND,
    PEMetricFilter.TOTAL_SALES,
    PEMetricFilter.EXPORTS,
  ]);

  const isMillionMetric = millionMetrics.has(metric);

  const isProdCapMetric = [
    PEMetricFilter.DOMESTIC_PRODUCTION,
    PEMetricFilter.TOTAL_SALES,
  ].includes(metric);

  let rawData: (number | null)[];
  let rawLabels: string[];

  if (isProdCapMetric) {
    rawLabels = prodCap.map((d) => {
      try { return format(new Date(d.date + 'T00:00:00'), 'MMM yyyy'); } catch { return d.date; }
    });
    rawData = prodCap.map((d) => {
      const keyMap: Record<string, keyof PEProductionCapacityItem> = {
        [PEMetricFilter.DOMESTIC_PRODUCTION]: 'production',
        [PEMetricFilter.TOTAL_SALES]: 'total_sales',
      };
      const k = keyMap[metric];
      const val = k ? d[k] : null;
      return val !== null && val !== undefined ? Number(val) : null;
    });
  } else {
    rawLabels = labels;
    rawData = metrics.map((d) => {
      let val = d[metric as keyof PEInventoryMetricsItem];
      if (metric === PEMetricFilter.INVENTORY_CHANGE) {
        val = d.inventory_change_monthly;
      }
      return val !== null && val !== undefined ? Number(val) : null;
    });
  }

  const isInventoryChange = metric === PEMetricFilter.INVENTORY_CHANGE;
  const transformedData = rawData.map((v) => {
    if (v === null || v === undefined) return null;
    return isMillionMetric ? v / 1_000_000 : v;
  });

  const barColors = transformedData.map((v) => {
    if (isInventoryChange && v !== null && v < 0) return 'rgba(220,38,38,0.9)';
    return 'rgba(132,204,22,0.85)';
  });

  const chartOptions: any = {
    ...CHART_DEFAULTS,
    plugins: {
      ...(CHART_DEFAULTS as any).plugins,
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const raw = Number(ctx.raw || 0);
            if (isMillionMetric) {
              return `${raw >= 0 ? '+' : ''}${formatMillionsFromBillions(raw)}`;
            }
            return `${raw}`;
          },
        },
      },
      datalabels: {
        // Hidden below 1024px; otherwise show when the visible window spans ≤ 3 years
        display: (context: any) => !hideDataLabels && isWithinThreeYearRange(context?.chart?.data?.labels ?? []),
        color: rs.offWhite,
        font: { size: 11, weight: 'bold' },
        anchor: 'center',
        align: 'center',
        formatter: (value: number | null) => {
          if (value === null || value === undefined) return '';
          if (metric === PEMetricFilter.DEMAND_RATE || metric === PEMetricFilter.OPERATING_RATES) {
            const numValue = Number(value);
            return numValue.toFixed(2);
          }
          if (metric === PEMetricFilter.INVENTORY_CHANGE) {
            const inMillions = value * 1000;
            const abs = Math.abs(inMillions);
            const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
            return `${Number(inMillions.toFixed(digits))}M`;
          }
          if (isMillionMetric) {
            return `${formatMillionsFromBillions(value)}`;
          }
          return `${Number(value.toFixed(0))}`;
        },
      } 
    },
    scales: {
      ...(CHART_DEFAULTS as any).scales,
      x: {
        ...(CHART_DEFAULTS as any).scales.x,
        ticks: {
          ...(CHART_DEFAULTS as any).scales.x.ticks,
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          callback: function (value: any, index: number) {
            const lbl = this.getLabelForValue(value);
            const d = parseDateLiteral(lbl);
            // <3yr window: every 6 months (MMM yyyy). Once dragged past the
            // 3-year mark, the whole axis switches to year-only. Applies to every metric.
            if (!isNaN(d.getTime())) {
              const yearOnly = isPastThreeYearMark(this.chart.data.labels as any[]);
              return inventoryChangeAxisLabel(d, yearOnly);
            }
            return index % 3 === 0 ? lbl : '';
          }
        }
      },
      y: {
        ...(CHART_DEFAULTS as any).scales.y,
        ticks: {
          ...(CHART_DEFAULTS as any).scales.y.ticks,
          callback: (value: any) => {
            const n = Number(value);
            if (isMillionMetric && [PEMetricFilter.DOMESTIC_PRODUCTION, PEMetricFilter.TOTAL_SALES, PEMetricFilter.DOMESTIC_DEMAND, PEMetricFilter.EXPORTS, PEMetricFilter.INVENTORY_CHANGE].includes(metric)) {
              return formatMillions(n);
            }
            return isMillionMetric ? formatBillions(n) : n;
          },
        },
        ...(isMillionMetric ? { title: { display: true, text: isInventoryChange ? 'Monthly lbs' : 'Daily lbs', color: rs.offWhite, font: { size: 12 } } } : {}),
      },
    },
  };

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs, isDark]);

  return (
    <ChartDateLimiter labels={rawLabels} datasets={[{ data: rawData, backgroundColor: barColors }]}>
      {(limitedLabels, limitedDatasets) => {
        const chartData = {
          labels: limitedLabels,
          datasets: [
            {
              label: PE_METRIC_LABELS[metric],
              data: (limitedDatasets[0]?.data ?? []).map((v: any) => {
                if (v === null || v === undefined) return null;
                return isMillionMetric ? Number(v) / 1_000_000 : Number(v);
              }),
              backgroundColor: limitedDatasets[0]?.backgroundColor ?? 'rgba(132,204,22,0.85)',
              borderRadius: 2,
            },
          ],
        };
        return (
          <div className="h-[500px]" data-testid={`pe-metric-chart-${metric}`}>
            <Bar data={chartData} options={chartOptions} plugins={[hoverLinePlugin]} />
          </div>
        );
      }}
    </ChartDateLimiter>
  );
};

const PETotalsTab: React.FC<PETotalsTabProps> = ({ resinType: initialResinType = 'pe', dateFrom, dateTo, inventoryChangeOnly = false, fullBleed = false, isOverviewPage = false }) => {
  const { isDark } = useTheme();
  const [selectedResin, setSelectedResin] = useState<ResinSubType>(initialResinType);
  const [selectedMetric, setSelectedMetric] = useState<PEMetricFilter>(PEMetricFilter.DAYS_INVENTORY);
  const [metrics, setMetrics] = useState<PEInventoryMetricsItem[]>([]);
  const [prodCap, setProdCap] = useState<PEProductionCapacityItem[]>([]);
  const [commentary, setCommentary] = useState<{ pe_inv_cum_gains_draws?: string; pe_prod_sales?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFullCommentary, setShowFullCommentary] = useState(false);

  // Responsive metric buttons: ≤500px show 1, ≤1100px show 3, ≤1280px show chevrons.
  const [scrollIndex, setScrollIndex] = useState(0);
  const [isSmall, setIsSmall] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1100px)').matches,
  );
  const [isMid, setIsMid] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1280px)').matches,
  );
  const [isTiny, setIsTiny] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 680px)').matches,
  );
  const [isSmallChart, setIsSmallChart] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const metricRowRef = useRef<HTMLDivElement>(null);
  const visibleCount = isTiny ? 1 : 3;

  useEffect(() => {
    const smallMq = window.matchMedia('(max-width: 1100px)');
    const midMq = window.matchMedia('(max-width: 1280px)');
    const tinyMq = window.matchMedia('(max-width: 680px)');
    const smallChartMq = window.matchMedia('(max-width: 749px)');
    const smallHandler = (e: MediaQueryListEvent) => { setIsSmall(e.matches); setScrollIndex(0); };
    const midHandler = (e: MediaQueryListEvent) => setIsMid(e.matches);
    const tinyHandler = (e: MediaQueryListEvent) => { setIsTiny(e.matches); setScrollIndex(0); };
    const smallChartHandler = (e: MediaQueryListEvent) => setIsSmallChart(e.matches);
    smallMq.addEventListener('change', smallHandler);
    midMq.addEventListener('change', midHandler);
    tinyMq.addEventListener('change', tinyHandler);
    smallChartMq.addEventListener('change', smallChartHandler);
    return () => {
      smallMq.removeEventListener('change', smallHandler);
      midMq.removeEventListener('change', midHandler);
      tinyMq.removeEventListener('change', tinyHandler);
      smallChartMq.removeEventListener('change', smallChartHandler);
    };
  }, []);

  const selectedMetricIndex = METRIC_TABS.indexOf(selectedMetric);

  const goToMetric = (newIndex: number) => {
    const clamped = Math.max(0, Math.min(METRIC_TABS.length - 1, newIndex));
    setSelectedMetric(METRIC_TABS[clamped]);
    if (isSmall) {
      setScrollIndex((i) => {
        if (clamped < i) return clamped;
        if (clamped > i + visibleCount - 1) return clamped - visibleCount + 1;
        return i;
      });
    } else {
      requestAnimationFrame(() => {
        const btn = metricRowRef.current?.children[clamped] as HTMLElement | undefined;
        btn?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      });
    }
  };

  const handleMetricPrev = () => goToMetric(selectedMetricIndex - 1);
  const handleMetricNext = () => goToMetric(selectedMetricIndex + 1);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [metricsData, prodCapData, commentaryData] = await Promise.all([
          getPEInventoryMetrics(selectedResin),
          getPEProductionCapacity(dateFrom, dateTo),
          getCachedACCFinalData('pe'),
        ]);
        setMetrics(metricsData);
        const filtered = prodCapData.filter(
          (d) => !d.resin_type || d.resin_type === selectedResin
        );
        setProdCap(filtered.length > 0 ? filtered : prodCapData.filter((d) => d.resin_type === selectedResin));
        if (commentaryData && commentaryData.length > 0) {
          const latest = commentaryData[commentaryData.length - 1];
          setCommentary({
            pe_inv_cum_gains_draws: latest?.pe_inv_cum_gains_draws || latest?.peInvCumGainsDraws,
            pe_prod_sales: latest?.pe_prod_sales || latest?.peProdSales,
          });
        }
      } catch (err) {
        console.error('PETotalsTab fetch error', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [selectedResin, dateFrom, dateTo]);

  const labels = metrics.map((d) => {
    try { return format(new Date(d.date + 'T00:00:00'), 'MMM yyyy'); } catch { return d.date; }
  });


  return (
    <FreeTrialFade enabled={isOverviewPage}>
    <div className={`flex flex-col gap-6 ${isSmall ? (fullBleed ? 'p-0 -mx-6' : 'p-0') : 'p-4'}`}>
      {/* Resin selector tabs — 2x2 grid below 500px, centered ≤1100px, left-aligned row above */}
      <div className={
        isTiny
          ? 'grid grid-cols-2 gap-2 w-fit mx-auto'
          : isSmall
          ? 'flex gap-2 flex-wrap justify-center'
          : 'flex gap-2 flex-wrap'
      }>
        {SUB_TYPES.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setSelectedResin(key)}
            data-testid={`pe-totals-resin-${key}`}
            className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
              selectedResin === key
                ? 'bg-rs-green text-black'
                : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[500px] text-rs-off-white/50">
          Loading PE data…
        </div>
      ) : inventoryChangeOnly ? (
        <div className="bg-rs-off-black rounded-lg p-3">
          <p className="text-rs-off-white/70 text-xs mb-2">{PE_METRIC_LABELS[PEMetricFilter.INVENTORY_CHANGE]}</p>
          <MetricChart
            metric={PEMetricFilter.INVENTORY_CHANGE}
            labels={labels}
            metrics={metrics}
            prodCap={prodCap}
          />
        </div>
      ) : (
        <>
          {/* Metric subtabs — responsive with chevrons (see top of component) */}
          <div className={`flex items-center gap-2 w-full ${isMid ? 'justify-center' : ''}`}>
            {isMid && (
              <button
                onClick={handleMetricPrev}
                disabled={selectedMetricIndex <= 0}
                className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
                aria-label="Previous metric"
              >
                <ChevronLeft size={28} strokeWidth={2.5} />
              </button>
            )}

            <div
              ref={metricRowRef}
              className={`flex gap-2 ${isSmall ? '' : isMid ? 'overflow-x-auto scrollbar-hide min-w-0' : 'overflow-x-auto scrollbar-hide flex-1'}`}
            >
              {(isSmall ? METRIC_TABS.slice(scrollIndex, scrollIndex + visibleCount) : METRIC_TABS).map((metric) => (
                <button
                  key={metric}
                  data-tour={`pe-metric-${metric}`}
                  onClick={() => setSelectedMetric(metric)}
                  className={`px-4 py-1.5 rounded text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    selectedMetric === metric
                      ? 'bg-rs-green text-black'
                      : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
                  }`}
                >
                  {PE_METRIC_LABELS[metric]}
                </button>
              ))}
            </div>

            {isMid && (
              <button
                onClick={handleMetricNext}
                disabled={selectedMetricIndex >= METRIC_TABS.length - 1}
                className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
                aria-label="Next metric"
              >
                <ChevronRight size={28} strokeWidth={2.5} />
              </button>
            )}
          </div>

          {/* Single chart card */}
          <div className={`bg-rs-off-black rounded-lg ${isSmall ? 'p-2' : 'p-4'}`}>
            <p className={`text-rs-off-white/70 text-sm mb-3 ${isSmallChart ? 'text-center' : ''}`}>
              {PE_METRIC_LABELS[selectedMetric]}
            </p>

            <MetricChart
              metric={selectedMetric}
              labels={labels}
              metrics={metrics}
              prodCap={prodCap}
              />
          </div>

          {/* Commentary panel */}
          {commentary && (commentary.pe_inv_cum_gains_draws || commentary.pe_prod_sales) && (
            <div className={`bg-rs-off-black rounded-lg ${isSmall ? 'p-2' : 'p-4'}`}>
              <button
                className="flex items-center gap-2 text-rs-green font-semibold text-sm mb-2"
                onClick={() => setShowFullCommentary((v) => !v)}
              >
                Commentary {showFullCommentary ? '▲' : '▼'}
              </button>
              {showFullCommentary && (
                <div className="flex flex-col gap-3 text-rs-off-white/80 text-sm leading-relaxed">
                  {commentary.pe_inv_cum_gains_draws && (
                    <div>
                      <p className="text-rs-light-gray font-medium mb-1">Inventory:</p>
                      <p dangerouslySetInnerHTML={{ __html: commentary.pe_inv_cum_gains_draws.replace(/\n/g, '<br/>') }} />
                    </div>
                  )}
                  {commentary.pe_prod_sales && (
                    <div>
                      <p className="text-rs-light-gray font-medium mb-1">Production & Sales:</p>
                      <p dangerouslySetInnerHTML={{ __html: commentary.pe_prod_sales.replace(/\n/g, '<br/>') }} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
      <p data-source-tag className={`mt-4 text-rs-highlight-gray text-xs ${fullBleed && isSmall ? 'px-4' : ''}`}>Source: American Chemistry Council® (ACC)</p>
    </div>
    </FreeTrialFade>
  );
};

export default PETotalsTab;
